import type {
  ModuleField,
  ModuleRecord,
  PlanTask,
  Project,
} from '@/types/domain';
import type { CsvColumn, CsvRow } from './csv-export';

export const planExportColumns: CsvColumn[] = [
  { key: 'projectNo', label: '项目编号' },
  { key: 'projectName', label: '项目名称' },
  { key: 'stage', label: '阶段' },
  { key: 'nodeId', label: '节点ID' },
  { key: 'parentId', label: '父节点ID' },
  { key: 'nodeType', label: '节点类型' },
  { key: 'name', label: '任务 / 活动名称' },
  { key: 'owner', label: '负责人' },
  { key: 'ownerRole', label: '责任角色' },
  { key: 'planStart', label: '计划开始' },
  { key: 'planEnd', label: '计划完成' },
  { key: 'actualEnd', label: '实际完成' },
  { key: 'statusLabel', label: '状态' },
  { key: 'status', label: '状态编码' },
  { key: 'progress', label: '进度（%）' },
  { key: 'applicable', label: '是否适用' },
  { key: 'keyDeliverable', label: '关键交付物' },
  { key: 'approvalRequired', label: '需要审批' },
  { key: 'approvalStatus', label: '审批状态' },
  { key: 'overdue', label: '是否逾期' },
  { key: 'outputs', label: '输出物' },
  { key: 'reviewRoles', label: '评审角色' },
  { key: 'relationKinds', label: '与当前用户关系' },
];

/**
 * progress：界面上显示的那个完成度（utils/plan-progress：做完算 100、阶段按下级汇总）。
 * 导出和屏幕要是同一个数（0930）；不传就退回库里存的原值。
 */
export function planTaskExportRow(
  project: Project,
  task: PlanTask,
  stage: string,
  statusLabel: string,
  progress?: number | null,
): CsvRow {
  return {
    projectNo: project.projectNo,
    projectName: project.projectName,
    stage,
    nodeId: task.id,
    parentId: task.parentId,
    nodeType: task.nodeType === 'phase' ? '阶段' : '任务',
    name: task.name,
    owner: task.ownerName,
    ownerRole: task.ownerRole,
    planStart: task.planStart,
    planEnd: task.planEnd,
    actualEnd: task.actualEnd,
    statusLabel,
    status: task.status,
    progress: progress === undefined ? task.progress : progress,
    applicable: task.applicable === false ? '否' : '是',
    keyDeliverable: task.keyDeliverable ? '是' : '否',
    approvalRequired: task.approvalRequired ? '是' : '否',
    approvalStatus: task.approvalStatus,
    overdue: task.overdue ? '是' : '否',
    outputs: task.outputs,
    reviewRoles: task.reviewRoles,
    relationKinds: task.relationKinds,
  };
}

function normalizedKey(key: string): string {
  return key.replaceAll('_', '').toLocaleLowerCase();
}

function snakeCase(key: string): string {
  return key.replace(/[A-Z]/gu, (letter) => `_${letter.toLocaleLowerCase()}`);
}

function camelCase(key: string): string {
  return key.replace(/_([a-z])/gu, (_, letter: string) => letter.toLocaleUpperCase());
}

export function readRecordValue(record: ModuleRecord, key: string): unknown {
  const aliases: Record<string, string[]> = {
    title: ['name'],
    dueDate: ['dueAt', 'due_at'],
    dueAt: ['dueDate', 'due_date'],
    projectId: ['project_id'],
    ownerName: ['owner_name'],
    parentId: ['parent_id'],
    updatedAt: ['updated_at'],
    createdAt: ['created_at'],
  };
  const candidates = [
    key,
    snakeCase(key),
    camelCase(key),
    ...(aliases[key] ?? []),
  ];
  const normalized = (
    record.normalized && typeof record.normalized === 'object'
      ? record.normalized
      : {}
  ) as Record<string, unknown>;
  for (const candidate of candidates) {
    const value = record[candidate] ?? normalized[candidate];
    if (value !== null && value !== undefined) return value;
  }
  return '';
}

export function recordExportColumns(
  columns: readonly CsvColumn[],
  fields: readonly ModuleField[],
  includeSource = false,
): CsvColumn[] {
  const result: CsvColumn[] = [{ key: 'recordId', label: '记录ID' }];
  if (includeSource) result.push({ key: '__sourceLabel', label: '数据类型' });
  const seen = new Set(result.map((column) => normalizedKey(column.key)));
  for (const column of [
    ...columns,
    ...fields.map((field) => ({ key: field.key, label: field.label })),
    { key: 'status', label: '状态编码' },
    { key: 'updatedAt', label: '最近更新' },
  ]) {
    const normalized = normalizedKey(column.key);
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(column);
  }
  return result;
}

export function recordExportRows(
  records: readonly ModuleRecord[],
  columns: readonly CsvColumn[],
): CsvRow[] {
  return records.map((record) => Object.fromEntries(columns.map((column) => [
    column.key,
    column.key === 'recordId' ? record.id : readRecordValue(record, column.key),
  ])));
}

